
import React from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import AnimateInView from '@/components/ui/animate-in-view';
import { Separator } from '@/components/ui/separator';
import { Check } from 'lucide-react';

interface Feature {
  title: string;
  description: string;
}

interface BespokeOverviewSectionProps {
  features: Feature[];
  image: string;
  buttonText: string;
  buttonLink: string;
}

const BespokeOverviewSection: React.FC<BespokeOverviewSectionProps> = ({ 
  features, 
  image, 
  buttonText, 
  buttonLink 
}) => {
  return (
    <section className="py-16 md:py-24 bg-white" aria-labelledby="bespoke-overview-heading">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <AnimateInView animation="fade-in">
            <div>
              <div className="flex flex-col items-start mb-6">
                <div className="h-0.5 bg-gold w-20 mb-4"></div>
                <h2 id="bespoke-overview-heading" className="text-3xl md:text-4xl font-serif text-forti-fi mb-6">
                  About Custom Solutions
                </h2>
              </div>
              <p className="text-lg text-forti-fi-charcoal mb-8">
                Our bespoke security solutions are designed for properties and installations that require
                specialized approaches beyond standard offerings. We create tailored security systems that
                address the unique challenges of complex sites while maintaining aesthetic harmony with 
                your property.
              </p>
              
              <div className="space-y-5 mb-8">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mr-3">
                      <div className="w-6 h-6 rounded-full bg-forti-fi-teal text-white flex items-center justify-center">
                        <Check className="h-4 w-4" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium text-forti-fi mb-1">{feature.title}</h3>
                      <p className="text-forti-fi-charcoal">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <Button asChild className="text-white">
                <Link to={buttonLink}>
                  {buttonText}
                </Link>
              </Button>
            </div>
          </AnimateInView>
          
          <AnimateInView animation="fade-in" delay={200} className="order-first md:order-last">
            <div className="rounded-lg overflow-hidden shadow-md">
              <img 
                src="/images/Solutions Bespoke Overview Option 1.png"
                alt="Bespoke security solution" 
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </AnimateInView>
        </div>
      </div>
    </section>
  );
};

export default BespokeOverviewSection;
